import React, { useEffect } from 'react';
import { Box, Button, Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Controller, useWatch } from 'react-hook-form';

import { AxlButton } from 'components/AxlButton';
import { useLocationLayoutForm } from 'hooks/Warehouse';
import { LOCATION_TYPES } from 'utils/warehouseLocationSetup';

const LocationForm = (props) => {
  const { formId, onSubmittingChange, assignedAreaByLocationId = {} } = props;
  const {
    control,
    fields,
    addLocation,
    removeLocation,
    isUniqueLocationId,
    submit,
    submitOverwrite,
    submitError,
    layoutExists,
    submitting,
  } = useLocationLayoutForm(props);

  const locations = useWatch({ control, name: 'locations' });
  // Accessible names follow the id typed so far, so screen readers say which location a control belongs to.
  const trimmedIds = (locations ?? []).map((location) => (location?.id ?? '').trim());
  const rowLabel = (index) => {
    const id = trimmedIds[index];
    if (!id) return `new location ${index + 1}`;
    // A duplicate id is invalid but can exist while being typed; keep the names distinct until it is fixed.
    return trimmedIds.indexOf(id) === trimmedIds.lastIndexOf(id) ? id : `${id}, row ${index + 1}`;
  };

  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);

  return (
    <Box component="form" id={formId} onSubmit={submit} noValidate>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Typography component="label" htmlFor="layout-name-input" variant="body2" sx={{ fontWeight: 600 }}>
            Layout Name
          </Typography>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Layout name is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id="layout-name-input"
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Layout Name"
                autoComplete="off"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                sx={{ mt: 0.5 }}
              />
            )}
          />
        </Grid>
      </Grid>
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
        Capacity of -1 means unlimited.
      </Typography>
      <Box sx={{ mt: 1 }}>
        {fields.length > 0 && (
          <Grid container spacing={1} sx={{ mb: 0.5 }}>
            <Grid size={3}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Location ID
              </Typography>
            </Grid>
            <Grid size={4}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Location Name
              </Typography>
            </Grid>
            <Grid size={2}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Capacity
              </Typography>
            </Grid>
            <Grid size={2}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Type
              </Typography>
            </Grid>
            <Grid size={1} />
          </Grid>
        )}
        {fields.map((row, index) => {
          const assignedArea = assignedAreaByLocationId[row.id];
          const label = rowLabel(index);
          return (
            <Grid container spacing={1} key={row.key} sx={{ alignItems: 'flex-start', mb: 1 }}>
              <Grid size={3}>
                <Controller
                  name={`locations.${index}.id`}
                  control={control}
                  rules={{ required: 'Location ids must not be empty', validate: isUniqueLocationId }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={!row.isNew}
                      placeholder="e.g. LAX-1"
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      slotProps={{ htmlInput: { 'aria-label': `Location ID, row ${index + 1}` } }}
                    />
                  )}
                />
              </Grid>
              <Grid size={4}>
                <Controller
                  name={`locations.${index}.name`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="e.g. Lane 1"
                      slotProps={{ htmlInput: { 'aria-label': `Name of ${label}` } }}
                    />
                  )}
                />
              </Grid>
              <Grid size={2}>
                <Controller
                  name={`locations.${index}.containerCapacity`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      variant="outlined"
                      size="small"
                      fullWidth
                      slotProps={{ htmlInput: { 'aria-label': `Capacity of ${label}` } }}
                    />
                  )}
                />
              </Grid>
              <Grid size={2}>
                <Controller
                  name={`locations.${index}.type`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      variant="outlined"
                      size="small"
                      fullWidth
                      slotProps={{ select: { SelectDisplayProps: { 'aria-label': `Type of ${label}` } } }}
                    >
                      {LOCATION_TYPES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid size={1}>
                <Tooltip
                  arrow
                  title={assignedArea ? `Assigned to “${assignedArea}” — unassign it in Storage Areas first.` : ''}
                >
                  <span>
                    <IconButton
                      aria-label={`Remove ${label}`}
                      onClick={() => removeLocation(index)}
                      size="small"
                      disabled={Boolean(assignedArea)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          );
        })}
        <Button variant="outlined" onClick={addLocation}>
          Add location
        </Button>
      </Box>
      {submitError && (
        <Typography color="error" variant="body2">
          {submitError}
        </Typography>
      )}
      {layoutExists && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <AxlButton variant="contained" onClick={submitOverwrite} loading={submitting}>
            OVERWRITE EXISTING LAYOUT
          </AxlButton>
        </Box>
      )}
    </Box>
  );
};

export default LocationForm;
